import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { KpiCard, KpiGrid } from 'molecules';

import { TCalendar, TONE, fmtMoney } from '../shared';
import { BalanceNowCard } from './BalanceNowCard';

/** Period caption row above the KPI grid (e.g. «Период: эта неделя»). */
const PeriodCaption = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
`;

export interface ISummaryStripProps {
  /** The whole calendar result; KPIs read its result-level totals. */
  calendar: TCalendar | null | undefined;
  /** Human-readable label for the active period (from `periodLabel`). */
  periodLabel: string;
  /** Refetch the main calendar after the «на счёте» snapshot is corrected. */
  onBalanceChanged?: () => void;
}

/**
 * The KPI row above the cockpit body: на счёте сейчас (live, editable) ·
 * придёт (awaited) · просрочено · к оплате · поступило · минимальный остаток.
 * Everything except the first card reads the active period's totals; the first
 * card is anchored to TODAY regardless of the horizon.
 */
export const SummaryStrip = memo<ISummaryStripProps>(
  ({ calendar, periodLabel, onBalanceChanged }) => {
    const { t } = useTranslation();
    const forPeriod = t('pages.paymentCalendar.summaryV2.forPeriod');

    return (
      <>
        <PeriodCaption>
          {t('pages.paymentCalendar.period.caption', { range: periodLabel })}
        </PeriodCaption>
        <KpiGrid columns={6}>
          <BalanceNowCard onChanged={onBalanceChanged} />
          <KpiCard
            label={t('pages.paymentCalendar.summaryV2.awaited')}
            value={fmtMoney(calendar?.totalAwaitedPln)}
            subLabel={forPeriod}
            accent={TONE.ok}
          />
          <KpiCard
            label={t('pages.paymentCalendar.summaryV2.overdue')}
            value={fmtMoney(calendar?.totalOverduePln)}
            subLabel={forPeriod}
            accent={TONE.danger}
          />
          <KpiCard
            label={t('pages.paymentCalendar.summaryV2.toPay')}
            value={fmtMoney(calendar?.totalToPayPln)}
            subLabel={forPeriod}
            accent={TONE.danger}
          />
          <KpiCard
            label={t('pages.paymentCalendar.summaryV2.received')}
            value={fmtMoney(calendar?.totalReceivedPln)}
            subLabel={forPeriod}
            accent={TONE.ok}
          />
          <KpiCard
            label={t('pages.paymentCalendar.summaryV2.minBalance')}
            value={fmtMoney(calendar?.minBalancePln)}
            subLabel={t('pages.paymentCalendar.summaryV2.minForPeriod')}
            accent={calendar?.hasShortfall ? TONE.danger : TONE.ok}
          />
        </KpiGrid>
      </>
    );
  },
);
